/**
 * Enhanced error handling utilities for API responses and user notifications
 */

export const parseApiError = (error) => {
  let errorMessage = 'An unexpected error occurred. Please try again.';
  let errorTitle = 'Error';
  let errorType = 'error';

  if (error.response?.data) {
    const errorData = error.response.data;
    const status = error.response.status;

    // Set error title based on status code
    switch (status) {
      case 400:
        errorTitle = 'Invalid Request';
        break;
      case 401:
        errorTitle = 'Authentication Required';
        errorMessage = 'Please log in to continue.';
        break;
      case 403:
        errorTitle = 'Access Denied';
        errorMessage = 'You do not have permission to perform this action.';
        break;
      case 404:
        errorTitle = 'Not Found';
        errorMessage = 'The requested resource was not found.';
        break;
      case 422:
        errorTitle = 'Validation Error';
        break;
      case 429:
        errorTitle = 'Rate Limit Exceeded';
        errorMessage = 'Too many requests. Please wait a moment and try again.';
        break;
      case 500:
        errorTitle = 'Server Error';
        errorMessage = 'A server error occurred. Please try again later.';
        break;
      case 503:
        errorTitle = 'Service Unavailable';
        errorMessage = 'The service is temporarily unavailable. Please try again later.';
        break;
      default:
        errorTitle = 'Error';
    }

    // Override with specific error message from API
    if (errorData.message) {
      errorMessage = errorData.message;
    }

    // Handle validation errors
    if (errorData.errors && typeof errorData.errors === 'object') {
      const validationErrors = Object.values(errorData.errors).flat();
      if (validationErrors.length > 0) {
        errorMessage = validationErrors.join(', ');
      }
    }

    // Handle specific banking error types
    if (errorData.error_code) {
      switch (errorData.error_code) {
        case 'INSUFFICIENT_FUNDS':
          errorTitle = 'Insufficient Funds';
          errorMessage = 'Your account balance is insufficient for this transaction.';
          break;
        case 'ACCOUNT_LOCKED':
          errorTitle = 'Account Locked';
          errorMessage = 'Your account has been temporarily locked. Please contact customer service.';
          break;
        case 'DAILY_LIMIT_EXCEEDED':
          errorTitle = 'Daily Limit Exceeded';
          errorMessage = 'You have exceeded your daily transaction limit.';
          break;
        case 'INVALID_RECIPIENT':
          errorTitle = 'Invalid Recipient';
          errorMessage = 'The recipient information is invalid or incomplete.';
          break;
        case 'DUPLICATE_TRANSACTION':
          errorTitle = 'Duplicate Transaction';
          errorMessage = 'This transaction appears to be a duplicate. Please wait before trying again.';
          errorType = 'warning';
          break;
        default:
          break;
      }
    }
  } else if (error.message) {
    errorMessage = error.message;
  }

  // Network errors
  if (error.code === 'NETWORK_ERROR' || !error.response) {
    errorTitle = 'Connection Error';
    errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  return {
    title: errorTitle,
    message: errorMessage,
    type: errorType,
    originalError: error
  };
};

/**
 * Handle API errors with notifications
 */
export const handleApiError = (error, notificationHook, customMessage = null) => {
  const parsedError = parseApiError(error);
  
  const message = customMessage || parsedError.message;
  const title = parsedError.title;
  const type = parsedError.type;
  
  if (type === 'warning') {
    notificationHook.showWarning(message, title, 6000);
  } else {
    notificationHook.showError(message, title, 8000);
  }
  
  return parsedError;
};

/**
 * Format success messages for transfers
 */
export const formatTransferSuccess = (transferType, amount, recipient = null) => {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);

  let message = '';
  let title = 'Transfer Successful';

  switch (transferType) {
    case 'between-accounts':
    case 'internal':
      message = `Transfer of ${formattedAmount} completed successfully between your accounts.`;
      break;
    case 'same-bank':
      message = `Transfer of ${formattedAmount} sent successfully${recipient ? ` to ${recipient}` : ''}.`;
      break;
    case 'external-bank':
    case 'external':
      message = `External transfer of ${formattedAmount} initiated successfully. It will be processed within 1-3 business days.`;
      title = 'Transfer Initiated';
      break;
    default:
      message = `Transfer of ${formattedAmount} completed successfully.`;
  }

  return { message, title };
};

/**
 * Format loading messages
 */
export const getLoadingMessage = (action) => {
  switch (action) {
    case 'transfer':
      return 'Processing your transfer...';
    case 'validation':
      return 'Validating recipient...';
    case 'account_lookup':
      return 'Looking up account information...';
    case 'balance_check':
      return 'Checking account balance...';
    default:
      return 'Processing...';
  }
}; 